import { branchIds, taskIndex } from './taskOrganization'
import { taskDueState } from './taskReminders'
import type { WritingTask } from './types'

export const isWritingTask = (task: WritingTask) => task.kind !== 'group'
export type TaskFilters = {
  userId: string
  scope: 'mine' | 'team' | 'personal'
  status: 'open' | 'all' | 'archive'
  projectId: string
  search: string
  due: 'all' | 'today' | 'overdue'
  today: string
}
export type TaskBranch = {
  task: WritingTask
  children: TaskBranch[]
  matched: boolean
  progress: { done: number; total: number }
}

export function taskAncestors(tasks: WritingTask[], id: string) {
  const byId = new Map(tasks.map(t => [t.id, t]))
  const result: WritingTask[] = []
  const seen = new Set([id])
  let item = byId.get(byId.get(id)?.parentId || '')
  while (item && !seen.has(item.id)) {
    seen.add(item.id)
    result.unshift(item)
    item = byId.get(item.parentId || '')
  }
  return result
}

export function taskParentOptions(tasks: WritingTask[], scope: WritingTask['scope'], userId: string, id = '') {
  const excluded = branchIds(tasks, id)
  return tasks.filter(t => t.scope === scope && (scope === 'team' || t.ownerId === userId) && !excluded.has(t.id))
}

export function taskTree(tasks: WritingTask[], filters: TaskFilters): TaskBranch[] {
  const { children } = taskIndex(tasks)
  const term = filters.search.trim().toLocaleLowerCase()
  const textMatches = (t: WritingTask) => !term || (t.title + ' ' + t.details).toLocaleLowerCase().includes(term)
  const sort = (a: WritingTask, b: WritingTask) =>
    Number(a.status === 'done') - Number(b.status === 'done') ||
    (a.dueDate || '9999').localeCompare(b.dueDate || '9999') ||
    b.createdAt.localeCompare(a.createdAt) || a.title.localeCompare(b.title)
  for (const list of children.values()) list.sort(sort)
  const keepGroups = new Set<string>()
  const roots: TaskBranch[] = [], seen = new Set<string>()
  type Visit = { task: WritingTask; ancestorMatch: boolean; output: TaskBranch[]; finish?: TaskBranch }
  const stack: Visit[] = (children.get('') || []).map(task => ({ task, ancestorMatch: false, output: roots })).reverse()
  while (stack.length) {
    const { task, ancestorMatch, output, finish } = stack.pop()!
    if (finish) {
      for (const child of finish.children) {
        finish.progress.total += child.progress.total
        finish.progress.done += child.progress.done
      }
      continue
    }
    if (seen.has(task.id)) continue
    seen.add(task.id)
    const found = ancestorMatch || textMatches(task)
    const scopeMatch = filters.scope === 'mine' ? task.assigneeId === filters.userId : task.scope === filters.scope
    const matches = scopeMatch && (!filters.projectId || task.projectId === filters.projectId) && found &&
      (filters.status === 'archive' ? task.archived : !task.archived && (filters.status === 'all' || task.status !== 'done')) &&
      (filters.due === 'all' || taskDueState(task, filters.today) === filters.due)
    const branch: TaskBranch = { task, children: [], matched: isWritingTask(task) && matches,
      progress: { total: isWritingTask(task) && !task.archived ? 1 : 0,
        done: isWritingTask(task) && !task.archived && task.status === 'done' ? 1 : 0 } }
    output.push(branch)
    stack.push({ task, ancestorMatch: found, output, finish: branch })
    const nested = children.get(task.id) || []
    for (let i = nested.length - 1; i >= 0; i--) stack.push({ task: nested[i], ancestorMatch: found, output: branch.children })
    // Keep empty manual groups visible alongside matching work.
    if (!nested.length && task.kind === 'group' && !task.reference && matches && filters.status !== 'archive')
      keepGroups.add(task.id)
  }
  // Filter after counting all descendants, so progress includes completed work.
  const order: TaskBranch[] = [], pending = [...roots]
  while (pending.length) { const next = pending.pop()!; order.push(next); for (const child of next.children) pending.push(child) }
  const visible = new Set<string>()
  for (let index = order.length - 1; index >= 0; index--) {
    const branch = order[index]
    branch.children = branch.children.filter(child => visible.has(child.task.id))
    if (branch.matched || branch.children.length || keepGroups.has(branch.task.id)) visible.add(branch.task.id)
  }
  return roots.filter(branch => visible.has(branch.task.id))
}

export function matchingTaskCount(tree: TaskBranch[]): number {
  let count = 0
  const stack = [...tree]
  while (stack.length) { const branch = stack.pop()!; count += Number(branch.matched); for (const child of branch.children) stack.push(child) }
  return count
}

export function visibleTaskBranches(tree: TaskBranch[], collapsed: Set<string>) {
  const result: { branch: TaskBranch; depth: number }[] = []
  const stack = tree.map(branch => ({ branch, depth: 0 })).reverse()
  while (stack.length) {
    const row = stack.pop()!; result.push(row)
    if (!collapsed.has(row.branch.task.id)) for (let index = row.branch.children.length - 1; index >= 0; index--)
      stack.push({ branch: row.branch.children[index], depth: row.depth + 1 })
  }
  return result
}
